import React from "react";
import { format, parseISO } from "date-fns";
import { formatLongDate } from "#app/utils/dateFormat.js";
import { useLocale } from "#app/context/LocaleContext.jsx";

import {
  Alert,
  AlertDescription,
  List,
  ListItem,
  ListIcon,
  Text,
  Icon,
  Box,
  CloseButton,
  useDisclosure,
  Button,
  Spinner,
} from "@chakra-ui/react";

import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  MdGroup as UsersIcon,
  MdUnfoldMore as MaximizeIcon,
  MdEventAvailable as OpeningsIcon,
  MdCalendarToday as LatestIcon,
} from "react-icons/md";

import { HelpPopover } from "#app/components/index.jsx";
import { maybePluralizeNoun } from "#app/utils/words.js";
import { formatAvailabilityRequestDate } from "#app/utils/dates.js";

export function ChanceAlert({
  isEnabled,
  query,
  defaultIsOpen = false,
  buttonText = "Fill out the form to see live stats",
  buttonProps = {},
  ...rest
}) {
  const locale = useLocale();
  const { isOpen, onClose, onOpen } = useDisclosure({
    defaultIsOpen,
  });

  const { isFetching, isPlaceholderData, isError, data } = query;

  if (isError || data?.fieldErrors?.length) {
    return (
      <Box bg="bg-canvas" {...rest}>
        <Button
          isDisabled
          borderRadius={0}
          width="100%"
          variant="secondary"
          rightIcon={<Icon as={MaximizeIcon} boxSize={6} />}
          {...buttonProps}
        >
          Error fetching live stats
        </Button>
      </Box>
    );
  }

  if (!isEnabled || !data) {
    return (
      <Box bg="bg-canvas" {...rest}>
        <Button
          isDisabled={!isFetching}
          borderRadius={0}
          width="100%"
          variant="secondary"
          rightIcon={
            isFetching ? (
              <Spinner size="sm" />
            ) : (
              <Icon as={MaximizeIcon} boxSize={6} />
            )
          }
          {...buttonProps}
        >
          {isFetching ? "Loading stats..." : buttonText}
        </Button>
      </Box>
    );
  }

  const { max, latest, openings, notifications } = data;

  // Collapsed view - show objective summary. The openings count is every
  // opening anyone watching these sites/dates has caught, for all time, so the
  // label says "past" to keep it from reading as openings this user missed.
  if (!isOpen) {
    return (
      <Box bg="bg-canvas" {...rest}>
        <Button
          onClick={onOpen}
          borderRadius={0}
          width="100%"
          variant="secondary"
          rightIcon={<Icon as={MaximizeIcon} boxSize={6} />}
          data-testid="stats-button"
          {...buttonProps}
        >
          {maybePluralizeNoun(max, "user")} watching &middot;{" "}
          {maybePluralizeNoun(openings, "past opening")}
          {isFetching && <Spinner size="xs" ml={2} />}
        </Button>
      </Box>
    );
  }

  // Expanded view
  return (
    <Box bg="bg-canvas" {...rest}>
      <Alert bg="bg-canvas">
        <Box flexGrow={1}>
          <AlertDescription>
            <Text mb={2} as="strong">
              Live stats
              {isFetching && <Spinner size="sm" ml={2} />}
            </Text>

            <Text mb={2} color="fg" fontSize="sm">
              For your selected sites and dates, across everyone watching
            </Text>
            <List>
              <ListItem data-testid="stats-users">
                <ListIcon as={UsersIcon} boxSize={5} />
                <Text as="strong" color="accent" whiteSpace="nowrap">
                  {maybePluralizeNoun(max, "other user")}
                </Text>{" "}
                watching
              </ListItem>
              <ListItem>
                <ListIcon as={OpeningsIcon} boxSize={5} />
                <Text
                  as="strong"
                  color="accent"
                  data-testid="stats-openings"
                  whiteSpace="nowrap"
                >
                  {maybePluralizeNoun(openings, "past opening")}
                </Text>{" "}
                caught for anyone watching
              </ListItem>
              {latest && (
                <ListItem data-testid="stats-latest">
                  <ListIcon as={LatestIcon} boxSize={5} />
                  <Text as="strong" color="accent" whiteSpace="nowrap">
                    {formatLongDate(parseISO(latest), locale)}
                  </Text>{" "}
                  was the most recent
                </ListItem>
              )}
            </List>
            <Text mt={2} color="subtle" fontSize="sm">
              Includes openings from before your request, and ones that
              didn&apos;t fit your filters or closed between your checks. Past
              openings are not indicative of future results.
            </Text>
          </AlertDescription>

          {/* Learn more popover with all the details */}
          <HelpPopover
            buttonText="Learn more"
            variant="secondary"
            size="xs"
            mt={2}
            contentProps={{
              ml: 4,
            }}
            header="What these numbers mean"
            body={
              <>
                <List spacing={2}>
                  <ListItem>
                    <Text as="strong" color="accent" whiteSpace="nowrap">
                      {maybePluralizeNoun(max, "other user")}{" "}
                    </Text>
                    {max === 1 ? "is" : "are"} watching similar criteria, and{" "}
                    may also be notified, depending on which site/date is found.
                  </ListItem>
                  <ListItem>
                    Across everyone who has ever watched these sites and dates,
                    we've found{" "}
                    <Text as="strong" color="accent" whiteSpace="nowrap">
                      {maybePluralizeNoun(openings, "opening")}
                    </Text>{" "}
                    (unique site/date), combined into{" "}
                    <Text as="strong" color="accent" whiteSpace="nowrap">
                      {maybePluralizeNoun(notifications, "notification")}
                    </Text>
                    . These aren&apos;t openings sent to you: your alerts only
                    include openings that match your request, found while
                    it&apos;s active.
                  </ListItem>
                  <ListItem>
                    <Text as="em" color="subtle" fontSize="sm">
                      Cancellations are random and may never happen.
                    </Text>
                  </ListItem>
                </List>
              </>
            }
          />
        </Box>
        <CloseButton
          alignSelf="flex-start"
          position="relative"
          right={-1}
          top={-1}
          onClick={onClose}
        />
      </Alert>
    </Box>
  );
}

export function FormChanceAlert({ facilityId, formContext, ...rest }) {
  const { watch } = formContext;

  const [selectedDates, selectedSiteIds, minStayLength] = watch([
    "selectedDates",
    "selectedSiteIds",
    "minStayLength",
  ]);

  const selectedDatesNoTimestamp = selectedDates.map((date) =>
    format(date, "yyyy-MM-dd"),
  );

  const isEnabled =
    selectedDates.length > 0 &&
    selectedSiteIds.length > 0 &&
    typeof minStayLength === "number";

  const queryKey = [
    "FormChanceAlert",
    selectedDatesNoTimestamp.join(","),
    selectedSiteIds.join(","),
    minStayLength,
  ];

  const query = useQuery({
    enabled: isEnabled,
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({
        facilityId,
        selectedDates: selectedDatesNoTimestamp.join(","),
        selectedSiteIds: selectedSiteIds.join(","),
        minStayLength,
      });
      const res = await fetch(`/api/stats?${params}`);
      if (!res.ok) throw new Error(`Stats fetch failed: ${res.status}`);
      return res.json();
    },
    staleTime: 60_000,
    // Keep previous data while fetching to prevent flickering
    placeholderData: keepPreviousData,
  });

  return (
    <ChanceAlert
      isEnabled={isEnabled}
      query={query}
      {...rest}
      data-testid="form-chance-alert"
    />
  );
}

export function RequestChanceAlert({ availabilityRequest, ...rest }) {
  const { id, facilityId, selectedSiteIds, selectedDates, minStayLength } =
    availabilityRequest;

  const selectedDatesNoTimestamp = selectedDates.map((date) =>
    formatAvailabilityRequestDate(date),
  );

  const queryKey = ["RequestChanceAlert", id];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({
        facilityId,
        selectedDates: selectedDatesNoTimestamp.join(","),
        selectedSiteIds: selectedSiteIds.join(","),
        minStayLength,
      });
      const res = await fetch(`/api/stats?${params}`);
      if (!res.ok) throw new Error(`Stats fetch failed: ${res.status}`);
      return res.json();
    },
    staleTime: 60_000,
    // Keep previous data while fetching to prevent flickering
    placeholderData: keepPreviousData,
  });

  return (
    <ChanceAlert
      isEnabled={true}
      defaultIsOpen={true}
      query={query}
      buttonText="Loading live stats"
      buttonProps={{
        borderRadius: "md",
      }}
      {...rest}
      data-testid="request-chance-alert"
    />
  );
}
